import type {FC, SVGProps} from 'react';

import Color from 'color';
import styled, {css} from 'styled-components';

import type {ThemeColor} from '../../../../styles';
import type {StyledProps} from './types';

import ChatSvg from '../../../../assets/icons/empty-state/chat.svg';
import ChatPrimarySvg from '../../../../assets/icons/primary/Chat.svg';
import StopSvg from '../../../../assets/icons/primary/Stop.svg';
import TimeCircleSvg from '../../../../assets/icons/secondary/Time Circle.svg';
import {getColor, ms, square} from '../../../../styles';
import {Button} from '../../../../uikit/Button';
import {Text as UIKitText} from '../../../../uikit/Text';
import {View} from '../../../../uikit/View';
import {platform} from '../../../../utils/platform';

export const Container = styled(View)<
  StyledProps & {
    isForCoach?: boolean;
  }
>`
  padding: ${ms(12)} ${ms(platform.isNative ? 20 : 16)};
  gap: ${ms(10)};
  border-radius: ${ms(platform.isNative ? 8 : 10)};
  border: 1px solid ${getColor('border2')};
  background-color: ${p =>
    Color(getColor('background1')(p)).alpha(0.5).toString()};
  backdrop-filter: blur(${ms(4)});
  align-items: center;

  ${p =>
    p.type === 'start' &&
    css`
      border-color: ${getColor(p.isForCoach ? 'global1' : 'global2')};
      background-color: ${getColor(p.isForCoach ? 'global1' : 'global2')};
    `}

  ${platform.isNative &&
  css`
    flex-direction: column;
    border-width: 0;
    align-items: flex-start;
  `}

  ${p =>
    platform.isNative &&
    p.type !== 'start' &&
    css`
      background-color: ${getColor('background2')};
      border-radius: 0;
    `}
`;

export const ChatIcon = styled(ChatSvg).attrs(p => ({
  fill: getColor('icon2')(p),
}))`
  ${square(48)};
`;

export const Content = styled(View)`
  flex-direction: column;
  gap: ${ms(4)};

  ${platform.isWeb &&
  css`
    flex: 1;
  `}
`;

export const SessionInfoRow = styled(View)`
  width: 100%;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: ${ms(8)};
`;

export const Title = styled(UIKitText).attrs<StyledProps>({
  weight: 'semibold',
})<StyledProps>`
  font-size: ${ms(16)};
  line-height: ${ms(24)};
  letter-spacing: ${ms(-0.41)};

  ${p =>
    p.type === 'start' &&
    css`
      color: ${getColor('text2')(p)};
    `}
`;

const SText = styled(UIKitText)<StyledProps>`
  font-size: ${ms(14)};
  line-height: ${ms(20)};
  letter-spacing: ${ms(-0.41)};

  ${p =>
    p.type === 'start' &&
    css`
      color: ${getColor('text2')(p)};
    `}
`;

export {SText as Text};

export const TimeBeforeEndContainer = styled(View)`
  flex-direction: row;
  align-items: center;
  gap: ${ms(4)};
`;

export const TimeBeforeEndIcon = styled(TimeCircleSvg).attrs<{
  isCritical?: boolean;
}>(p => ({
  fill: getColor(p.isCritical ? 'error' : 'icon3')(p),
}))`
  ${square(16)};
`;

export const TimeBeforeEndText = styled(SText)<{
  isCritical?: boolean;
}>`
  color: ${p => getColor(p.isCritical ? 'error' : 'text3')(p)};
`;

export const ActionButtonText = styled(UIKitText).attrs<StyledProps>({
  weight: 'semibold',
})<StyledProps>`
  font-size: ${ms(14)};
  line-height: ${ms(20)};
  letter-spacing: ${ms(-0.41)};
  color: ${getColor('text1')};

  ${p =>
    p.type === 'inProgress' &&
    css`
      color: ${getColor('error')(p)};
    `}

  ${p =>
    p.type === 'overdue' &&
    css`
      color: ${getColor('text2')(p)};
    `}
`;

export const ActionButton = styled(Button).attrs<StyledProps>({
  scheme: 'gray',
  size: 'm',
})<StyledProps>`
  padding: ${ms(10)} ${ms(16)};
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: ${ms(8)};
  background-color: ${getColor('background1')};
  border-radius: ${ms(12)};
  border: 1px solid ${getColor('border1')};
  box-shadow: 0 ${ms(4)} ${ms(20)} rgba(1, 1, 1, 0.05);
  elevation: 5;

  ${platform.isNative &&
  css`
    width: 100%;
  `}

  ${p =>
    p.type === 'start' &&
    css`
      padding-inline: ${ms(26)};
    `}

  ${p =>
    p.type === 'overdue' &&
    css`
      background-color: ${getColor('error')};

      &:hover {
        background-color: ${Color(getColor('error')(p)).alpha(0.8).toString()};
      }
    `}
`;

export const ActionButtonIcon = styled(({type, ...rest}) => {
  let Icon: FC<SVGProps<SVGSVGElement>> | null = null;

  switch (type) {
    case 'inProgress':
    case 'overdue':
      Icon = StopSvg;
      break;
    case 'start':
      Icon = ChatPrimarySvg;
      break;
    default:
      break;
  }

  return Icon ? <Icon type={type} {...rest} /> : null;
}).attrs(p => {
  let fillColor: ThemeColor = 'global1';

  switch (p.type) {
    case 'inProgress':
      fillColor = 'error';
      break;
    case 'overdue':
      fillColor = 'icon2';
      break;
    case 'start':
      fillColor = 'icon4';
      break;
    default:
      break;
  }

  return {
    fill: getColor(fillColor)(p),
  };
})`
  ${square(20)};
`;
